import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../environment";
import { getAuthHeaders } from "../../utils/authUtils";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import male from "../../assets/male.png";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../feature-module/uiInterface/base-ui/alertservice";

interface ApprovalStage {
  approval_policy_stage_id: number;
  stage_name: string;
  stage_order: number;
}

interface ApprovalInstanceStage {
  approval_policy_entity_instance_stage_id: number;
  status: string;
  started_datetime: string | null;
  completed_datetime: string | null;
  tenant_id: number;
  approval_policy_entity_instance_id: number;
  approval_policy_stage_id: number;
  approval_policy_stage: ApprovalStage;
}

interface Approver {
  user_id: number | null;
  full_name: string;
  avatar_url: string | null;
  email: string;
}

interface ApprovalActorType {
  approval_actor_type_id: number;
  approval_actor_type_key: string;
  approval_actor_type_display_name: string;
}

interface StageApprover {
  approval_policy_stage_approver_id?: number;
  approval_policy_entity_instance_stage_approver_id?: number;
  approver_user_id?: number;
  approver_email?: string | null;
  approver_identities?: (number | string)[];
  tenant_id?: number;
  approval_policy_stage_id?: number;
  approval_actor_type_id?: number;
  approval_actor_type?: ApprovalActorType;
  approvers?: Approver[];
  approver_detail?: Approver;
  status?: string;
  comment?: string | null;
  action_datetime?: string | null;
}

interface ApprovalFlowProps {
  tenant_id: string | null;
  related_entity_id?: number;
  related_entity_name: string;
  currentUserId?: number;
}

const approvalFlowStyles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px",
    minHeight: "300px",
    backgroundColor: "#fff",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "28px",
  },
  headerIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "20px",
    flexShrink: 0,
  },
  headerTitle: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
  },
  headerSubtitle: {
    fontSize: "13px",
    color: "#94a3b8",
    margin: 0,
  },
  timeline: {
    position: "relative" as const,
    paddingLeft: "0",
  },
  stageCard: {
    position: "relative" as const,
    display: "flex",
    gap: "16px",
    marginBottom: "8px",
    cursor: "pointer",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  stageIndicatorCol: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    width: "40px",
    flexShrink: 0,
  },
  stageContent: {
    flex: 1,
    borderRadius: "12px",
    padding: "16px 20px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  stageContentActive: {
    border: "1px solid #818cf8",
    background: "linear-gradient(135deg, #f0f0ff 0%, #faf5ff 100%)",
    boxShadow: "0 4px 16px rgba(99, 102, 241, 0.12)",
  },
  stageName: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
  },
  stageMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginTop: "6px",
    flexWrap: "wrap" as const,
  },
  stageMeta: {
    fontSize: "12px",
    color: "#94a3b8",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  detailPanel: {
    overflow: "hidden",
    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  detailPanelInner: {
    padding: "16px 20px 8px 20px",
    borderTop: "1px solid #e2e8f0",
    marginTop: "12px",
  },
  approverGroupLabel: {
    fontSize: "12px",
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.05em",
    color: "#6366f1",
    marginBottom: "12px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  approverCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 14px",
    borderRadius: "10px",
    background: "#f8fafc",
    border: "1px solid #f1f5f9",
    marginBottom: "8px",
    transition: "all 0.2s ease",
  },
  approverAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    objectFit: "cover" as const,
    border: "2px solid #e2e8f0",
    flexShrink: 0,
  },
  approverName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#1e293b",
    margin: 0,
    lineHeight: 1.3,
  },
  approverEmail: {
    fontSize: "12px",
    color: "#94a3b8",
    margin: 0,
    lineHeight: 1.3,
  },
  externalBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    fontWeight: 500,
    color: "#f59e0b",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "6px",
    padding: "2px 8px",
  },
  emptyApprovers: {
    textAlign: "center" as const,
    padding: "20px",
    color: "#94a3b8",
    fontSize: "13px",
  },
  connector: {
    width: "2px",
    flex: 1,
    minHeight: "12px",
    background: "#e2e8f0",
    transition: "background 0.3s ease",
  },
  connectorActive: {
    background: "linear-gradient(180deg, #6366f1 0%, #a78bfa 100%)",
  },
  noDataContainer: {
    textAlign: "center" as const,
    padding: "60px 20px",
  },
  noDataIcon: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #f0f0ff 0%, #faf5ff 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    fontSize: "32px",
    color: "#6366f1",
  },
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "completed":
    case "approved":
      return {
        label: "Approved",
        bg: "#ecfdf5",
        color: "#059669",
        border: "#a7f3d0",
        icon: "ti ti-circle-check-filled",
        dotBg: "linear-gradient(135deg, #059669, #10b981)",
      };
    case "pending":
      return {
        label: "Pending",
        bg: "#fef3c7",
        color: "#d97706",
        border: "#fde68a",
        icon: "ti ti-clock",
        dotBg: "linear-gradient(135deg, #d97706, #f59e0b)",
      };
    case "waiting":
      return {
        label: "Waiting",
        bg: "#f1f5f9",
        color: "#64748b",
        border: "#e2e8f0",
        icon: "ti ti-hourglass-low",
        dotBg: "linear-gradient(135deg, #94a3b8, #cbd5e1)",
      };
    case "rejected":
      return {
        label: "Rejected",
        bg: "#fef2f2",
        color: "#dc2626",
        border: "#fecaca",
        icon: "ti ti-circle-x-filled",
        dotBg: "linear-gradient(135deg, #dc2626, #ef4444)",
      };
    default:
      return {
        label: status,
        bg: "#f8fafc",
        color: "#64748b",
        border: "#e2e8f0",
        icon: "ti ti-circle-dot",
        dotBg: "linear-gradient(135deg, #94a3b8, #cbd5e1)",
      };
  }
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ApprovalFlow: React.FC<ApprovalFlowProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  currentUserId,
}) => {
  const [stages, setStages] = useState<ApprovalInstanceStage[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);
  const [selectedStage, setSelectedStage] = useState<ApprovalInstanceStage | null>(null);
  const [approvers, setApprovers] = useState<StageApprover[]>([]);
  const [approversLoading, setApproversLoading] = useState(false);
  const [instanceId, setInstanceId] = useState<number | null>(null);
  const [actionComment, setActionComment] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInstanceAndStages = async () => {
    if (!related_entity_id || !tenant_id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const instanceRes = await axios.get(
        `${API_BASE_URL}/approval-policy-entity-instance/?tenant_id=${tenant_id}&related_entity_id=${related_entity_id}&related_entity_name=${related_entity_name}`,
        { headers: getAuthHeaders() }
      );

      if (instanceRes.data.count === 0 || !instanceRes.data.results?.length) {
        setStages([]);
        setLoading(false);
        return;
      }

      const currentInstanceId = instanceRes.data.results[0].approval_policy_entity_instance_id;
      setInstanceId(currentInstanceId);

      const stagesRes = await axios.get(
        `${API_BASE_URL}/approval-policy-entity-instance-stage/?tenant_id=${tenant_id}&approval_policy_entity_instance_id=${currentInstanceId}`,
        { headers: getAuthHeaders() }
      );

      const sortedStages = (stagesRes.data.results || []).sort(
        (a: ApprovalInstanceStage, b: ApprovalInstanceStage) =>
          a.approval_policy_stage.stage_order - b.approval_policy_stage.stage_order
      );

      setStages(sortedStages);
    } catch (err: any) {
      console.error("Error fetching approval stages:", err);
      setError("Failed to load approval flow data");
    } finally {
      setLoading(false);
    }
  };

  const { hasBeenActivated } = useTabVisibility({
    tabId: "approval-flow",
    onTabActivated: () => {
      if (!dataLoaded && tenant_id && related_entity_id) {
        fetchInstanceAndStages();
        setDataLoaded(true);
      }
    },
  });

  useEffect(() => {
    if (hasBeenActivated && !dataLoaded && tenant_id && related_entity_id) {
      fetchInstanceAndStages();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, related_entity_id, tenant_id, related_entity_name]);

  const fetchStageApprovers = async (stage: ApprovalInstanceStage) => {
    setApproversLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/approval-policy-entity-instance-stage-approver/?tenant_id=${tenant_id}&approval_policy_entity_instance_stage_id=${stage.approval_policy_entity_instance_stage_id}`,
        { headers: getAuthHeaders() }
      );
      setApprovers(res.data.results || []);
    } catch (err) {
      console.error("Error fetching approver details:", err);
      setApprovers([]);
    } finally {
      setApproversLoading(false);
    }
  };

  const refreshStages = async () => {
    if (!instanceId || !tenant_id) return;
    try {
      const stagesRes = await axios.get(
        `${API_BASE_URL}/approval-policy-entity-instance-stage/?tenant_id=${tenant_id}&approval_policy_entity_instance_id=${instanceId}`,
        { headers: getAuthHeaders() }
      );
      const sortedStages = (stagesRes.data.results || []).sort(
        (a: ApprovalInstanceStage, b: ApprovalInstanceStage) =>
          a.approval_policy_stage.stage_order - b.approval_policy_stage.stage_order
      );
      setStages(sortedStages);

      if (selectedStage) {
        const updated = sortedStages.find(
          (s: ApprovalInstanceStage) =>
            s.approval_policy_entity_instance_stage_id ===
            selectedStage.approval_policy_entity_instance_stage_id
        );
        if (updated) setSelectedStage(updated);
      }
    } catch (err) {
      console.error("Error refreshing stages:", err);
    }
  };

  const handleStageClick = async (stage: ApprovalInstanceStage) => {
    const stageId = stage.approval_policy_stage_id;

    if (selectedStageId === stageId) {
      setSelectedStageId(null);
      setSelectedStage(null);
      setApprovers([]);
      setActionComment("");
      return;
    }

    setSelectedStageId(stageId);
    setSelectedStage(stage);
    await fetchStageApprovers(stage);
  };

  const handleApprovalAction = async (
    approverId: number,
    action: "APPROVED" | "REJECTED"
  ) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/approval-action/submit/`,
        {
          from_crm: true,
          approval_policy_entity_instance_stage_approver_id: approverId,
          action,
          comment: actionComment || undefined,
        },
        { headers: getAuthHeaders() }
      );
      setActionComment("");
      showSuccessAlert(`Action submitted successfully.`);
      await refreshStages();
      if (selectedStage) {
        const updatedStage = stages.find(
          (s) =>
            s.approval_policy_entity_instance_stage_id ===
            selectedStage.approval_policy_entity_instance_stage_id
        );
        await fetchStageApprovers(updatedStage || selectedStage);
      }
    } catch (err: any) {
      console.error("Error submitting approval action:", err);
      showErrorAlert(
        err.response?.data?.message || "Failed to submit approval action."
      );
    } finally {
      setActionLoading(false);
    }
  };

  const renderApproverDetails = (stageApprover: StageApprover, stageStatus: string) => {
    if (stageApprover.approver_detail) {
      const approver = stageApprover.approver_detail;
      const status = stageApprover.status || "pending";
      const comment = stageApprover.comment;
      const approverId = stageApprover.approval_policy_entity_instance_stage_approver_id;
      const isCurrentUser =
        currentUserId != null && stageApprover.approver_user_id === currentUserId;
      const showActionUI = isCurrentUser && status === "pending" && approverId != null;
      const canAct = showActionUI && stageStatus !== "waiting";

      return (
        <div>
          <div style={approvalFlowStyles.approverGroupLabel}>
            <i className="ti ti-user" />
            Approver
          </div>
          <div
            style={{
              ...approvalFlowStyles.approverCard,
              flexDirection: "column",
              alignItems: "stretch",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.borderColor = "#e2e8f0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f8fafc";
              e.currentTarget.style.borderColor = "#f1f5f9";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <img
                src={approver.avatar_url || male}
                alt={approver.full_name || "Approver"}
                style={approvalFlowStyles.approverAvatar}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = male;
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={approvalFlowStyles.approverName}>
                  {approver.full_name || "Unknown User"}
                </p>
                <p style={approvalFlowStyles.approverEmail}>{approver.email}</p>
              </div>
              {status && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color:
                      status === "completed" || status === "approved"
                        ? "#059669"
                        : status === "pending"
                          ? "#d97706"
                          : "#dc2626",
                    background:
                      status === "completed" || status === "approved"
                        ? "#ecfdf5"
                        : status === "pending"
                          ? "#fef3c7"
                          : "#fef2f2",
                    border: `1px solid ${status === "completed" || status === "approved"
                      ? "#a7f3d0"
                      : status === "pending"
                        ? "#fde68a"
                        : "#fecaca"
                      }`,
                    borderRadius: "6px",
                    padding: "2px 8px",
                    textTransform: "capitalize",
                  }}
                >
                  {status}
                </span>
              )}
            </div>
            {comment && (
              <div
                style={{
                  marginTop: "4px",
                  padding: "10px",
                  background: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  color: "#475569",
                }}
              >
                <strong style={{ color: "#1e293b", marginRight: "4px" }}>
                  Comment:
                </strong>
                {comment}
              </div>
            )}
            {showActionUI && (
              <div
                style={{ marginTop: "8px", borderTop: "1px solid #e2e8f0", paddingTop: "12px" }}
                onClick={(e) => e.stopPropagation()}
                title={!canAct ? "Action controls will enable when above stages are approved" : undefined}
              >
                {!canAct && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      marginBottom: "10px",
                      padding: "8px 12px",
                      borderRadius: "8px",
                      background: "#f1f5f9",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    <i className="ti ti-hourglass-low" style={{ fontSize: "14px" }} />
                    This stage currently has waiting status.
                  </div>
                )}
                <textarea
                  value={actionComment}
                  onChange={(e) => setActionComment(e.target.value)}
                  placeholder="Add a comment (optional)"
                  rows={2}
                  style={{
                    width: "100%",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    padding: "8px 12px",
                    fontSize: "13px",
                    color: "#1e293b",
                    resize: "vertical",
                    outline: "none",
                    marginBottom: "10px",
                    background: canAct ? "#fff" : "#f8fafc",
                    opacity: canAct ? 1 : 0.6,
                  }}
                  disabled={!canAct || actionLoading}
                />
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "none",
                      background: !canAct || actionLoading ? "#d1fae5" : "#059669",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: !canAct || actionLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      opacity: !canAct ? 0.5 : 1,
                    }}
                    disabled={!canAct || actionLoading}
                    onClick={() => canAct && handleApprovalAction(approverId!, "APPROVED")}
                  >
                    {actionLoading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <i className="ti ti-circle-check" />
                    )}
                    Approve
                  </button>
                  <button
                    style={{
                      flex: 1,
                      padding: "8px 16px",
                      borderRadius: "8px",
                      border: "none",
                      background: !canAct || actionLoading ? "#fee2e2" : "#dc2626",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: "13px",
                      cursor: !canAct || actionLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      opacity: !canAct ? 0.5 : 1,
                    }}
                    disabled={!canAct || actionLoading}
                    onClick={() => canAct && handleApprovalAction(approverId!, "REJECTED")}
                  >
                    {actionLoading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      <i className="ti ti-circle-x" />
                    )}
                    Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (stageApprover.approver_email) {
      const status = stageApprover.status || "pending";
      const comment = stageApprover.comment;

      return (
        <div>
          <div style={approvalFlowStyles.approverGroupLabel}>
            <i className="ti ti-mail" />
            External Approver
          </div>
          <div
            style={{
              ...approvalFlowStyles.approverCard,
              flexDirection: "column",
              alignItems: "stretch",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f1f5f9";
              e.currentTarget.style.borderColor = "#e2e8f0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#f8fafc";
              e.currentTarget.style.borderColor = "#f1f5f9";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "16px",
                  color: "#d97706",
                }}
              >
                <i className="ti ti-mail" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={approvalFlowStyles.approverEmail}>{stageApprover.approver_email}</p>
              </div>
              {status && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    color:
                      status === "completed" || status === "approved"
                        ? "#059669"
                        : status === "pending"
                          ? "#d97706"
                          : "#dc2626",
                    background:
                      status === "completed" || status === "approved"
                        ? "#ecfdf5"
                        : status === "pending"
                          ? "#fef3c7"
                          : "#fef2f2",
                    border: `1px solid ${status === "completed" || status === "approved"
                      ? "#a7f3d0"
                      : status === "pending"
                        ? "#fde68a"
                        : "#fecaca"
                      }`,
                    borderRadius: "6px",
                    padding: "2px 8px",
                    textTransform: "capitalize",
                  }}
                >
                  {status}
                </span>
              )}
            </div>
            {comment && (
              <div
                style={{
                  marginTop: "4px",
                  padding: "10px",
                  background: "#fff",
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  fontSize: "13px",
                  color: "#475569",
                }}
              >
                <strong style={{ color: "#1e293b", marginRight: "4px" }}>
                  Comment:
                </strong>
                {comment}
              </div>
            )}
          </div>
        </div>
      );
    }

    const actorKey = stageApprover.approval_actor_type?.approval_actor_type_key;
    const actorLabel =
      stageApprover.approval_actor_type?.approval_actor_type_display_name ||
      "Approver";

    if (actorKey === "EXTERNAL_APPROVER") {
      const emails = (stageApprover.approver_identities || []).filter(
        (id) => typeof id === "string"
      ) as string[];

      if (emails.length === 0) {
        return (
          <div style={approvalFlowStyles.emptyApprovers}>
            <i
              className="ti ti-user-off"
              style={{ fontSize: "20px", marginBottom: "4px", display: "block" }}
            />
            No external approvers configured
          </div>
        );
      }

      return (
        <div>
          <div style={approvalFlowStyles.approverGroupLabel}>
            <i className="ti ti-mail" />
            {actorLabel}
          </div>
          {emails.map((email, idx) => (
            <div
              key={idx}
              style={approvalFlowStyles.approverCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#f1f5f9";
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #fef3c7, #fde68a)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "16px",
                  color: "#d97706",
                }}
              >
                <i className="ti ti-mail" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={approvalFlowStyles.approverEmail}>{email}</p>
                <span style={approvalFlowStyles.externalBadge}>
                  <i className="ti ti-external-link" style={{ fontSize: "10px" }} />
                  External
                </span>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (stageApprover.approvers && stageApprover.approvers.length > 0) {
      return (
        <div>
          <div style={approvalFlowStyles.approverGroupLabel}>
            <i className="ti ti-users" />
            {actorLabel}
          </div>
          {stageApprover.approvers.map((approver, idx) => (
            <div
              key={approver.user_id || idx}
              style={approvalFlowStyles.approverCard}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#f1f5f9";
                e.currentTarget.style.borderColor = "#e2e8f0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f8fafc";
                e.currentTarget.style.borderColor = "#f1f5f9";
              }}
            >
              <img
                src={approver.avatar_url || male}
                alt={approver.full_name || "Approver"}
                style={approvalFlowStyles.approverAvatar}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = male;
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={approvalFlowStyles.approverName}>
                  {approver.full_name || "Unknown User"}
                </p>
                <p style={approvalFlowStyles.approverEmail}>{approver.email}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        <div style={approvalFlowStyles.approverGroupLabel}>
          <i className="ti ti-user-circle" />
          {actorLabel}
        </div>
        <div style={approvalFlowStyles.emptyApprovers}>
          <i
            className="ti ti-user-search"
            style={{ fontSize: "20px", marginBottom: "4px", display: "block" }}
          />
          Approver will be determined automatically
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="tab-pane fade" id="approval-flow">
        <div className="contact-activity">
          <div
            style={{
              ...approvalFlowStyles.container,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "2.5rem", height: "2.5rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p style={{ marginTop: "12px", color: "#94a3b8", fontSize: "14px" }}>
                Loading approval flow...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tab-pane fade" id="approval-flow">
        <div className="contact-activity">
          <div style={approvalFlowStyles.container}>
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i
                className="ti ti-alert-circle me-2"
                style={{ fontSize: "20px" }}
              />
              <div>
                <strong>Error:</strong> {error}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="tab-pane fade" id="approval-flow">
        <div className="contact-activity">
          <div style={approvalFlowStyles.container}>
            <div style={approvalFlowStyles.noDataContainer}>
              <div style={approvalFlowStyles.noDataIcon}>
                <i className="ti ti-git-branch" />
              </div>
              <h5 style={{ fontWeight: 600, color: "#1e293b", marginBottom: "8px" }}>
                No Approval Flow
              </h5>
              <p
                style={{
                  color: "#94a3b8",
                  fontSize: "14px",
                  maxWidth: "360px",
                  margin: "0 auto",
                }}
              >
                No approval process has been initiated for this{" "}
                {related_entity_name} yet.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tab-pane fade" id="approval-flow">
      <div className="contact-activity">
        <div style={approvalFlowStyles.container}>
          <div style={approvalFlowStyles.header}>
            <div style={approvalFlowStyles.headerIcon}>
              <i className="ti ti-git-branch" />
            </div>
            <div>
              <h5 style={approvalFlowStyles.headerTitle}>Approval Flow</h5>
              <p style={approvalFlowStyles.headerSubtitle}>
                {stages.filter((s) => s.status === "approved").length} of{" "}
                {stages.length} stages completed
              </p>
            </div>
          </div>

          <div style={approvalFlowStyles.timeline}>
            {stages.map((stage, index) => {
              const statusConfig = getStatusConfig(stage.status);
              const isSelected = selectedStageId === stage.approval_policy_stage_id;
              const isLast = index === stages.length - 1;
              const isCompleted = stage.status === "completed";

              return (
                <div key={stage.approval_policy_entity_instance_stage_id}>
                  <div
                    style={approvalFlowStyles.stageCard}
                    onClick={() => handleStageClick(stage)}
                  >
                    <div style={approvalFlowStyles.stageIndicatorCol}>
                      <div
                        style={{
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          background: statusConfig.dotBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: "16px",
                          flexShrink: 0,
                          boxShadow: `0 2px 8px ${statusConfig.color}33`,
                          transition: "transform 0.2s ease",
                          transform: isSelected ? "scale(1.15)" : "scale(1)",
                        }}
                      >
                        <i className={statusConfig.icon} />
                      </div>
                      {!isLast && (
                        <div
                          style={{
                            ...approvalFlowStyles.connector,
                            ...(isCompleted ? approvalFlowStyles.connectorActive : {}),
                          }}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        ...approvalFlowStyles.stageContent,
                        ...(isSelected ? approvalFlowStyles.stageContentActive : {}),
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div
                          style={{ display: "flex", alignItems: "center", gap: "10px" }}
                        >
                          <span style={approvalFlowStyles.stageName}>
                            {stage.approval_policy_stage.stage_name}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: statusConfig.color,
                              background: statusConfig.bg,
                              border: `1px solid ${statusConfig.border}`,
                              borderRadius: "6px",
                              padding: "2px 10px",
                              textTransform: "capitalize",
                            }}
                          >
                            {statusConfig.label}
                          </span>
                        </div>
                        <i
                          className={`ti ti-chevron-${isSelected ? "up" : "down"}`}
                          style={{
                            fontSize: "18px",
                            color: "#94a3b8",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </div>

                      <div style={approvalFlowStyles.stageMetaRow}>
                        {stage.started_datetime && (
                          <span style={approvalFlowStyles.stageMeta}>
                            <i
                              className="ti ti-clock"
                              style={{ fontSize: "13px" }}
                            />
                            Started: {formatDateTime(stage.started_datetime)}
                          </span>
                        )}
                        {stage.completed_datetime && (
                          <span style={approvalFlowStyles.stageMeta}>
                            <i
                              className="ti ti-circle-check"
                              style={{ fontSize: "13px" }}
                            />
                            Completed: {formatDateTime(stage.completed_datetime)}
                          </span>
                        )}
                      </div>

                      {isSelected && (
                        <div style={approvalFlowStyles.detailPanel}>
                          <div style={approvalFlowStyles.detailPanelInner}>
                            {approversLoading ? (
                              <div style={{ textAlign: "center", padding: "20px" }}>
                                <div
                                  className="spinner-border spinner-border-sm text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                                <p
                                  style={{
                                    fontSize: "13px",
                                    color: "#94a3b8",
                                    marginTop: "8px",
                                  }}
                                >
                                  Loading approver details...
                                </p>
                              </div>
                            ) : approvers.length > 0 ? (
                              <div>
                                {approvers.map((sa, idx) => (
                                  <div
                                    key={
                                      sa.approval_policy_entity_instance_stage_approver_id ||
                                      sa.approval_policy_stage_approver_id ||
                                      idx
                                    }
                                    style={{ marginBottom: "12px" }}
                                  >
                                    {renderApproverDetails(sa, stage.status)}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div style={approvalFlowStyles.emptyApprovers}>
                                <i
                                  className="ti ti-user-off"
                                  style={{
                                    fontSize: "24px",
                                    marginBottom: "6px",
                                    display: "block",
                                  }}
                                />
                                No approver information available
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalFlow;
